import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Send, Copy, Check, AlertTriangle, Sparkles, Clock, Briefcase, Link2, Users, PlayCircle, Loader2 } from 'lucide-react';

import {
  cleanTemplateTitle,
  DEFAULT_PERSONA_ID,
  type DemoStartPayload,
  type InterviewTemplate,
  type InviteCreatePayload,
  type InviteCreateResponse,
  type Persona,
  type SeniorityLevel,
} from '../../api/interview';
import PersonaVoicePlayButton from './PersonaVoicePlayButton';

export interface AtsConnectionLite {
  provider: string;
  is_active: boolean;
  auto_push?: boolean;
}

export interface AtsJobLite {
  ats_job_id: string;
  title: string;
  department?: string | null;
  location?: string | null;
}

export interface AtsCandidateOption {
  ats_candidate_id: string;
  ats_application_id?: string | null;
  ats_job_id?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  status?: string | null;
}

interface Props {
  isOpen: boolean;
  templates: InterviewTemplate[];
  personas: Persona[];
  onClose: () => void;
  onCreate: (payload: InviteCreatePayload) => Promise<InviteCreateResponse>;
  /**
   * Pre-select this template's family when the modal opens. Used when
   * recruiters click "Invite" directly from a template card / detail
   * modal so they don't have to scroll the dropdown to find the same
   * template again.
   */
  presetTemplate?: InterviewTemplate | null;
  /**
   * Start a demo of the currently selected template instead of sending
   * an invite. Lets the recruiter preview the candidate experience right
   * from the invite flow. Optional -- button hidden when not provided.
   */
  onDemo?: (payload: DemoStartPayload) => void;
  demoStarting?: string | null;

  // -------- Optional ATS context --------------------------------------
  // All three props are optional so this modal stays usable in any
  // context (no ATS connected, ATS API down, etc.) — we just gracefully
  // skip the picker / dropdown when the data isn't there.
  atsConnections?: AtsConnectionLite[];
  atsJobs?: AtsJobLite[];
  onLoadAtsCandidates?: (params: {
    provider: string;
    ats_job_id?: string | null;
    limit?: number;
  }) => Promise<AtsCandidateOption[]>;
}

const EXPIRY_OPTIONS = [
  { hours: 24,   label_de: '24 Stunden',  label_en: '24 hours' },
  { hours: 72,   label_de: '3 Tage',      label_en: '3 days' },
  { hours: 168,  label_de: '7 Tage',      label_en: '7 days' },
  { hours: 336,  label_de: '14 Tage',     label_en: '14 days' },
];

const DURATION_OPTIONS: { minutes: 30 | 45 | 60; label_de: string; label_en: string }[] = [
  { minutes: 30, label_de: '30 Min.', label_en: '30 min' },
  { minutes: 45, label_de: '45 Min.', label_en: '45 min' },
  { minutes: 60, label_de: '60 Min.', label_en: '60 min' },
];

function snapInviteDuration(raw: number): 30 | 45 | 60 {
  if (raw <= 37) return 30;
  if (raw <= 52) return 45;
  return 60;
}

/**
 * Career-level options shown in the invite. The recruiter MUST pick
 * one — it drives interview depth + scorecard latte (mapped server-side
 * to {student, junior, mid, senior} in `career_level.py`).
 */
const SENIORITY_OPTIONS: { id: SeniorityLevel; label_de: string; label_en: string }[] = [
  { id: 'intern',    label_de: 'Studi / Werkstudent', label_en: 'Student / Intern' },
  { id: 'junior',    label_de: 'Junior',              label_en: 'Junior' },
  { id: 'mid',       label_de: 'Mid-Level',           label_en: 'Mid-level' },
  { id: 'senior',    label_de: 'Senior',              label_en: 'Senior' },
  { id: 'staff',     label_de: 'Staff',               label_en: 'Staff' },
  { id: 'principal', label_de: 'Principal',           label_en: 'Principal' },
  { id: 'lead',      label_de: 'Lead / Head',         label_en: 'Lead / Head' },
];

interface TemplateFamily {
  key: string;
  variants: Record<string, InterviewTemplate>;
  preview: InterviewTemplate;
  availableLangs: string[];
}

// Local alias so the existing call sites in this file keep working.
// The shared helper also strips Cevee Pool naming-convention suffixes.
const cleanTitle = cleanTemplateTitle;

const InviteCreateModal: React.FC<Props> = ({
  isOpen,
  templates,
  personas,
  onClose,
  onCreate,
  presetTemplate,
  onDemo,
  demoStarting,
  atsConnections,
  atsJobs,
  onLoadAtsCandidates,
}) => {
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';

  const usableTemplates = useMemo(
    () => templates.filter((t) => t.is_active),
    [templates],
  );

  /**
   * Group templates by family_key (DE+EN siblings collapse into one card).
   * Templates without a family_key (custom) stay as their own family of
   * size 1 keyed by id.
   */
  const families = useMemo<TemplateFamily[]>(() => {
    const byKey = new Map<string, TemplateFamily>();
    for (const tpl of usableTemplates) {
      const key = tpl.family_key || `id:${tpl.id}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.variants[tpl.language] = tpl;
        if (!existing.availableLangs.includes(tpl.language)) {
          existing.availableLangs.push(tpl.language);
        }
        // Prefer EN as preview for the canonical title; fall back to first
        if (tpl.language === 'en') existing.preview = tpl;
      } else {
        byKey.set(key, {
          key,
          variants: { [tpl.language]: tpl },
          preview: tpl,
          availableLangs: [tpl.language],
        });
      }
    }
    // Stable order: public Cevee templates first (by title), then custom (by title)
    return Array.from(byKey.values()).sort((a, b) => {
      if (a.preview.is_public !== b.preview.is_public) {
        return a.preview.is_public ? -1 : 1;
      }
      return cleanTitle(a.preview.title).localeCompare(cleanTitle(b.preview.title));
    });
  }, [usableTemplates]);

  // Preset-driven initial selection: when a recruiter opens the modal
  // from a template card / detail view, we pre-pick that template's
  // family so they don't have to find it again in the dropdown. The
  // preset's own language (if available) wins over the UI's language
  // -- a recruiter on a DE template likely wants to send the DE
  // interview, even if they're browsing the UI in English.
  const presetFamilyKey = presetTemplate?.family_key
    || (presetTemplate ? `id:${presetTemplate.id}` : '');
  const [familyKey, setFamilyKey] = useState<string>(presetFamilyKey || families[0]?.key || '');
  const [interviewLang, setInterviewLang] = useState<'de' | 'en'>(
    (presetTemplate?.language === 'de' || presetTemplate?.language === 'en')
      ? presetTemplate.language
      : lang,
  );

  const selectedFamily = useMemo(
    () => families.find((f) => f.key === familyKey) || families[0],
    [families, familyKey],
  );

  /**
   * Resolve the actual underlying template_id from family + chosen
   * interview language. If the chosen language isn't available (e.g.
   * recruiter's custom template only exists in DE), fall back to the
   * preview variant and keep the picker honest by showing only the
   * available languages in the toggle.
   */
  const resolvedTemplate = useMemo<InterviewTemplate | undefined>(() => {
    if (!selectedFamily) return undefined;
    return (
      selectedFamily.variants[interviewLang] ||
      selectedFamily.variants[selectedFamily.availableLangs[0]] ||
      selectedFamily.preview
    );
  }, [selectedFamily, interviewLang]);

  const templateId = resolvedTemplate?.id || '';

  /**
   * If the currently selected language isn't available in the chosen
   * family (e.g. custom template only in DE), nudge the toggle to a
   * language we actually have so the toggle UI never lies.
   */
  useEffect(() => {
    if (!selectedFamily) return;
    if (selectedFamily.availableLangs.includes(interviewLang)) return;
    const next = selectedFamily.availableLangs.includes(lang)
      ? (lang as 'de' | 'en')
      : (selectedFamily.availableLangs[0] as 'de' | 'en');
    setInterviewLang(next);
  }, [selectedFamily, interviewLang, lang]);
  const [email, setEmail] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [roleSeniority, setRoleSeniority] = useState<SeniorityLevel>('mid');
  const [roleDescription, setRoleDescription] = useState('');
  const [personaOverride, setPersonaOverride] = useState<Persona['id'] | ''>('');
  const [expiry, setExpiry] = useState(168);
  const [duration, setDuration] = useState<30 | 45 | 60>(45);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [success, setSuccess] = useState<InviteCreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // ATS picker state. atsProvider derives from the first active
  // connection (today: only Ashby). atsJobId is what we attach to the
  // invite + what scopes the candidate picker.
  const activeAtsConnection = useMemo(
    () => (atsConnections || []).find((c) => c.is_active) || null,
    [atsConnections],
  );
  const atsProvider = activeAtsConnection?.provider || null;
  const hasAtsJobs = !!(atsJobs && atsJobs.length > 0);
  const [atsJobId, setAtsJobId] = useState<string>('');
  const [showAtsPicker, setShowAtsPicker] = useState(false);
  const [atsCandidates, setAtsCandidates] = useState<AtsCandidateOption[]>([]);
  const [atsLoading, setAtsLoading] = useState(false);
  const [atsError, setAtsError] = useState<string | null>(null);

  const selectedAtsJob = useMemo(
    () => (atsJobs || []).find((j) => j.ats_job_id === atsJobId) || null,
    [atsJobs, atsJobId],
  );

  const selectedTemplate = resolvedTemplate;
  const isPoolTemplate = !!selectedTemplate?.sampling_strategy;
  const isCeveeOfficial = !!selectedTemplate?.is_cevee_official;
  const effectivePersonaId = personaOverride || selectedTemplate?.persona_id || DEFAULT_PERSONA_ID;

  useEffect(() => {
    if (!selectedTemplate) return;
    setDuration(snapInviteDuration(Number(selectedTemplate.duration_minutes) || 45));
    // Cevee Pre-Screen templates lock the level too: snap to the template's
    // configured difficulty so the recruiter doesn't accidentally raise the
    // bar past the standardised setting. Backend's SeniorityLevel doesn't
    // know "student" -- map it to "intern" (closest peer) so the invite
    // POST doesn't fail with a 422.
    if (selectedTemplate.is_cevee_official && selectedTemplate.difficulty) {
      const lvl = String(selectedTemplate.difficulty);
      const mapped: SeniorityLevel | null =
        lvl === 'student' ? 'intern'
        : lvl === 'junior' ? 'junior'
        : lvl === 'mid' ? 'mid'
        : lvl === 'senior' ? 'senior'
        : null;
      if (mapped) setRoleSeniority(mapped);
    }
  }, [selectedTemplate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const t = (de: string, en: string) => (lang === 'de' ? de : en);
  const effectivePersona = personas.find((p) => p.id === effectivePersonaId);

  const reset = () => {
    setEmail('');
    setRoleTitle('');
    setRoleSeniority('mid');
    setRoleDescription('');
    setMessage('');
    setPersonaOverride('');
    setSuccess(null);
    setCopied(false);
    setError(null);
    setAtsJobId('');
    setShowAtsPicker(false);
    setAtsCandidates([]);
    setAtsError(null);
  };

  const handleSubmit = async () => {
    if (!templateId || !email.trim() || roleTitle.trim().length < 2 || !roleSeniority) {
      setError(t(
        'Template, E-Mail, Rolle und Level werden benötigt.',
        'Template, email, role and level are required.',
      ));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await onCreate({
        template_id: templateId,
        invited_email: email.trim(),
        role_title: roleTitle.trim(),
        role_seniority: roleSeniority,
        role_description: roleDescription.trim() || null,
        persona_id: personaOverride || undefined,
        invite_message: message.trim() || undefined,
        expires_in_hours: expiry,
        target_duration_minutes: duration,
        visibility_scope: 'company',
        // Only attach an ATS link if BOTH job + provider are known.
        // We never want a stray provider without a job id.
        ats_job_id: atsJobId && atsProvider ? atsJobId : null,
        ats_provider: atsJobId && atsProvider ? atsProvider : null,
      });
      setSuccess(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const openAtsPicker = async () => {
    if (!onLoadAtsCandidates || !atsProvider) return;
    setShowAtsPicker(true);
    setAtsLoading(true);
    setAtsError(null);
    try {
      const list = await onLoadAtsCandidates({
        provider: atsProvider,
        ats_job_id: atsJobId || null,
        limit: 50,
      });
      setAtsCandidates(list);
    } catch (e) {
      setAtsError(e instanceof Error ? e.message : String(e));
    } finally {
      setAtsLoading(false);
    }
  };

  const pickAtsCandidate = (c: AtsCandidateOption) => {
    if (c.email) setEmail(c.email);
    // Auto-fill role title from the selected job if the field is empty
    // -- recruiter can still edit afterwards.
    if (!roleTitle.trim() && selectedAtsJob?.title) {
      setRoleTitle(selectedAtsJob.title);
    }
    setShowAtsPicker(false);
  };

  const copyLink = async () => {
    if (!success?.invite_url) return;
    try {
      await navigator.clipboard.writeText(success.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-900/40 backdrop-blur-md px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-3xl border border-slate-200/70 shadow-[0_20px_60px_-12px_rgba(15,23,42,0.25)] max-w-2xl w-full my-auto relative">
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 tracking-tight">
              {success ? t('Einladung erstellt', 'Invite created') : t('Kandidat:in einladen', 'Invite a candidate')}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {success
                ? t('Email wurde versendet. Du kannst den Link auch direkt kopieren.', 'Email sent. You can also copy the link directly.')
                : t('Per Email mit Magic-Link. Login + Einwilligung erfolgen vor dem Interview.', 'Sent via email with a magic link. Login + consent happen before the interview.')}
            </p>
          </div>
          <button
            onClick={() => {
              if (success) reset();
              onClose();
            }}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!success && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
                <Field
                  label={t('Template', 'Template')}
                  hint={t(
                    'Cevee-Standards sind oben, eigene Templates darunter.',
                    'Cevee curated on top, your own templates below.',
                  )}
                >
                  <select
                    value={familyKey}
                    onChange={(e) => setFamilyKey(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    disabled={submitting}
                  >
                    {families.length === 0 && (
                      <option value="">{t('Kein aktives Template', 'No active template')}</option>
                    )}
                    {families.map((fam) => {
                      const cevee = fam.preview.is_public ? ` · ${t('Cevee Standard', 'Cevee curated')}` : '';
                      const pool = fam.preview.sampling_strategy ? ` · ${t('Pool', 'Pool')}` : '';
                      return (
                        <option key={fam.key} value={fam.key}>
                          {cleanTitle(fam.preview.title)}{cevee}{pool}
                        </option>
                      );
                    })}
                  </select>
                </Field>

                <Field
                  label={t('Interview-Sprache', 'Interview language')}
                  hint={t(
                    'Die KI spricht + bewertet in dieser Sprache.',
                    'The AI speaks + evaluates in this language.',
                  )}
                >
                  <div className="flex rounded-xl border border-slate-200 overflow-hidden text-sm">
                    {(['de', 'en'] as const).map((code) => {
                      const available = selectedFamily?.availableLangs.includes(code) ?? false;
                      const isActive = interviewLang === code;
                      return (
                        <button
                          key={code}
                          type="button"
                          disabled={!available || submitting}
                          onClick={() => setInterviewLang(code)}
                          className={`flex-1 px-3 py-2 font-medium transition-colors ${
                            isActive
                              ? 'bg-slate-900 text-white'
                              : available
                              ? 'bg-white text-slate-700 hover:bg-slate-50'
                              : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                          }`}
                        >
                          {code.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>

              {/* ATS link section. Three states:
                  (a) No connection → nudge (deep-link to /company-manage)
                  (b) Connection, no jobs → quiet hint, no dropdown
                  (c) Connection + jobs → job dropdown (the candidate
                      picker lives next to the email field below).
                  Sits right above "Concrete role" because picking an
                  ATS job pre-fills that field. */}
              {(!atsConnections || atsConnections.length === 0) && (
                <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 border border-dashed border-slate-200">
                  <Link2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-slate-600 leading-relaxed flex-1">
                    {t(
                      'Tipp: Verbinde dein ATS (z.B. Ashby) in den Firmen-Einstellungen, um Interviews direkt einer Stelle zuzuordnen und Kandidat:innen ohne Tippen zu uebernehmen.',
                      'Tip: connect your ATS (e.g. Ashby) in Company Settings to tag interviews with a job and pull candidates without typing.',
                    )}{' '}
                    <a
                      href="/company/manage"
                      className="text-pink-600 hover:underline font-medium"
                    >
                      {t('ATS verbinden →', 'Connect ATS →')}
                    </a>
                  </div>
                </div>
              )}

              {atsProvider && (
                <Field
                  label={t('ATS-Stelle (optional)', 'ATS job (optional)')}
                  hint={t(
                    'Verknuepft dieses Interview mit einer Stelle aus deinem ATS und uebernimmt den Titel als Rolle.',
                    'Links this interview to a job from your ATS and copies its title into the role field.',
                  )}
                >
                  <select
                    value={atsJobId}
                    onChange={(e) => {
                      const next = e.target.value;
                      setAtsJobId(next);
                      // Auto-fill role title from the picked job if the
                      // recruiter hasn't typed anything yet — keeps the
                      // common case "interview for THIS job" one click.
                      const job = (atsJobs || []).find(
                        (j) => j.ats_job_id === next,
                      );
                      if (job && !roleTitle.trim()) setRoleTitle(job.title);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    disabled={submitting || !hasAtsJobs}
                  >
                    <option value="">
                      {hasAtsJobs
                        ? t('Keine Verknuepfung', 'No link')
                        : t('Keine Jobs synchronisiert', 'No jobs synced')}
                    </option>
                    {(atsJobs || []).map((j) => (
                      <option key={j.ats_job_id} value={j.ats_job_id}>
                        {j.title}
                        {j.department ? ` · ${j.department}` : ''}
                        {j.location ? ` · ${j.location}` : ''}
                      </option>
                    ))}
                  </select>
                  {selectedAtsJob && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-100 text-[11px] text-slate-600">
                      <Briefcase className="w-3 h-3" />
                      {atsProvider.charAt(0).toUpperCase() + atsProvider.slice(1)} ·{' '}
                      {selectedAtsJob.title}
                    </div>
                  )}
                </Field>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
                <Field
                  label={t('Konkrete Rolle', 'Concrete role')}
                  hint={t(
                    'Pflicht. Die KI passt Follow-ups + Bewertung an diese Rolle an.',
                    'Required. The AI tailors follow-ups + scoring to this role.',
                  )}
                >
                  <input
                    type="text"
                    value={roleTitle}
                    onChange={(e) => setRoleTitle(e.target.value)}
                    placeholder={t(
                      'z.B. Senior Frontend Engineer',
                      'e.g. Senior Frontend Engineer',
                    )}
                    maxLength={200}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    disabled={submitting}
                  />
                </Field>

                <Field
                  label={isCeveeOfficial ? t('Level (fix)', 'Level (fixed)') : t('Level', 'Level')}
                  hint={isCeveeOfficial
                    ? t(
                        'Cevee Pre-Screens haben ein standardisiertes Level für Vergleichbarkeit.',
                        'Cevee Pre-Screens have a standardised level for score comparability.',
                      )
                    : t(
                        'Steuert KI-Tiefe und Bewertungs-Latte. Pflichtfeld.',
                        'Drives AI depth and scoring bar. Required.',
                      )}
                >
                  <select
                    value={roleSeniority}
                    onChange={(e) => setRoleSeniority(e.target.value as SeniorityLevel)}
                    className={[
                      'w-full px-3 py-2 border rounded-xl text-sm focus:outline-none focus:ring-2',
                      isCeveeOfficial
                        ? 'border-amber-300 bg-amber-50 text-amber-900 cursor-not-allowed'
                        : 'border-slate-200 focus:ring-pink-300',
                    ].join(' ')}
                    disabled={submitting || isCeveeOfficial}
                  >
                    {SENIORITY_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {lang === 'de' ? o.label_de : o.label_en}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field
                label={t('Job-Notizen (optional)', 'Role notes (optional)')}
                hint={t(
                  'Wichtigste Must-Haves / Auszug aus dem Job-Ad. Die KI bezieht das in ihre Follow-ups + Bewertung ein.',
                  'Top must-haves / a short job-ad excerpt. The AI weaves this into its follow-ups + evaluation.',
                )}
              >
                <textarea
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  rows={3}
                  maxLength={4000}
                  placeholder={t(
                    'z.B. Erfahrung mit React + Performance, Mentoring von Junior:innen, Cross-Team Kommunikation ...',
                    'e.g. experience with React + perf, mentoring juniors, cross-team comms ...',
                  )}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                  disabled={submitting}
                />
                <p className="text-[11px] text-slate-400 mt-1 text-right">{roleDescription.length} / 4000</p>
              </Field>

              {isPoolTemplate && !isCeveeOfficial && (
                <Field
                  label={t('Interview-Dauer', 'Interview duration')}
                  hint={t(
                    'Laenge pro Einladung (30 / 45 / 60 Min.), nicht am Template.',
                    'Length per invite (30 / 45 / 60 min), not on the template.',
                  )}
                >
                  <div className="flex flex-wrap gap-2">
                    {DURATION_OPTIONS.map((o) => {
                      const active = duration === o.minutes;
                      return (
                        <button
                          key={o.minutes}
                          type="button"
                          disabled={submitting}
                          onClick={() => setDuration(o.minutes)}
                          className={[
                            'px-4 py-2 rounded-xl text-sm font-semibold border transition-colors',
                            active
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300',
                          ].join(' ')}
                        >
                          {lang === 'de' ? o.label_de : o.label_en}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}
              {isCeveeOfficial && selectedTemplate && (
                <Field
                  label={t('Interview-Dauer (fix)', 'Interview duration (fixed)')}
                  hint={t(
                    'Cevee Pre-Screens haben eine standardisierte Dauer für Vergleichbarkeit.',
                    'Cevee Pre-Screens have a standardised duration for score comparability.',
                  )}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-amber-50 text-amber-900 ring-1 ring-amber-300">
                    <Clock className="w-4 h-4" />
                    {selectedTemplate.duration_minutes} {t('Min.', 'min')}
                  </div>
                </Field>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label={t('Kandidaten-Email', 'Candidate email')}
                  hint={
                    atsProvider && onLoadAtsCandidates
                      ? t(
                          'Tippen oder direkt aus deinem ATS uebernehmen.',
                          'Type it or pull a candidate from your ATS.',
                        )
                      : undefined
                  }
                >
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="alex@example.com"
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                      disabled={submitting}
                    />
                    {atsProvider && onLoadAtsCandidates && (
                      <button
                        type="button"
                        onClick={openAtsPicker}
                        disabled={submitting}
                        className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center gap-1.5 whitespace-nowrap"
                        title={
                          selectedAtsJob
                            ? t(
                                `Kandidat:in aus ${selectedAtsJob.title}`,
                                `Pick from ${selectedAtsJob.title}`,
                              )
                            : t('Kandidat:in aus ATS', 'Pick from ATS')
                        }
                      >
                        <Users className="w-4 h-4 text-slate-500" />
                        {t('Aus ATS', 'From ATS')}
                      </button>
                    )}
                  </div>
                </Field>

                <Field label={t('Gültig für', 'Valid for')}>
                  <select
                    value={expiry}
                    onChange={(e) => setExpiry(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                    disabled={submitting}
                  >
                    {EXPIRY_OPTIONS.map((o) => (
                      <option key={o.hours} value={o.hours}>
                        {lang === 'de' ? o.label_de : o.label_en}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field
                label={t('AI-Interviewerin', 'AI interviewer')}
                hint={
                  selectedTemplate
                    ? t(
                        `Standard vom Template: ${selectedTemplate.persona_id}. Du kannst hier wechseln.`,
                        `Template default: ${selectedTemplate.persona_id}. You can change it here.`,
                      )
                    : undefined
                }
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {personas.map((p) => {
                    const selected = (personaOverride || selectedTemplate?.persona_id) === p.id;
                    return (
                      <div
                        key={p.id}
                        className={[
                          'rounded-xl border px-3 py-2 transition-all relative',
                          selected
                            ? 'border-pink-300 bg-pink-50/40 shadow-[0_2px_8px_rgba(236,72,153,0.18)]'
                            : 'border-slate-200 hover:border-slate-300',
                        ].join(' ')}
                      >
                        {/* The whole tile is clickable to select / unselect.
                            The voice-preview button sits absolutely on top
                            so it can stop click-propagation to the tile. */}
                        <button
                          type="button"
                          onClick={() => setPersonaOverride(personaOverride === p.id ? '' : p.id)}
                          disabled={submitting}
                          className="w-full text-left"
                        >
                          <div className="flex items-center gap-2 pr-9">
                            <div
                              className={`w-6 h-6 rounded-full bg-gradient-to-br ${p.gradient} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}
                            >
                              {p.display_name[0]}
                            </div>
                            <span className="text-sm font-semibold text-slate-800 truncate">{p.display_name}</span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1 leading-tight pr-9">
                            {lang === 'de' ? p.vibe_de : p.vibe_en}
                          </p>
                        </button>
                        <div
                          className="absolute right-2 top-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PersonaVoicePlayButton
                            personaId={p.id}
                            language={interviewLang}
                            displayName={p.display_name}
                            voiceSpeed={p.voice_speed}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Field>

              <Field
                label={t('Persoenliche Nachricht (optional)', 'Personal message (optional)')}
                hint={t(
                  'Erscheint im Email + auf der Begruessungsseite.',
                  'Shown in the email and on the welcome page.',
                )}
              >
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t(
                    'Hi Alex, freue mich dich kennenzulernen ...',
                    'Hi Alex, looking forward to chatting ...',
                  )}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                  disabled={submitting}
                />
                <p className="text-[11px] text-slate-400 mt-1 text-right">{message.length} / 2000</p>
              </Field>

              {effectivePersona && (
                <div className="flex items-start gap-3 p-3 rounded-xl bg-gradient-to-r from-slate-50 to-pink-50/40 border border-slate-100">
                  <Sparkles className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-slate-600 leading-relaxed">
                    {t('Wird geführt von', 'Hosted by')}{' '}
                    <strong className="text-slate-800">{effectivePersona.display_name}</strong>{' '}
                    &middot;{' '}
                    {lang === 'de' ? effectivePersona.vibe_de : effectivePersona.vibe_en}
                  </div>
                </div>
              )}
            </>
          )}

          {success && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-200">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-800">
                    {t('Eingeladen', 'Invited')}: {success.session.invited_email || email}
                  </p>
                  <p className="text-xs text-emerald-700 mt-0.5">
                    {t('Einladungs-Email wird gerade versendet.', 'Invitation email is on its way.')}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t('Magic-Link', 'Magic link')}
                </label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    readOnly
                    value={success.invite_url}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-xs font-mono bg-slate-50/60 text-slate-700"
                  />
                  <button
                    onClick={copyLink}
                    className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1.5"
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-500" />}
                    {copied ? t('Kopiert', 'Copied') : t('Kopieren', 'Copy')}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  {t(
                    'Aus Sicherheitsgruenden wird der Token nur jetzt einmalig angezeigt. Speicherst du nur einen Hash davon.',
                    'For security the raw token is only shown right now. We only store its hash.',
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {showAtsPicker && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm rounded-3xl"
            onClick={() => setShowAtsPicker(false)}
          >
            <div
              className="bg-white rounded-2xl border border-slate-200 shadow-xl w-[min(560px,90%)] max-h-[80%] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {t('Kandidat:in aus', 'Pick candidate from')}{' '}
                    {(atsProvider || '').charAt(0).toUpperCase()}
                    {(atsProvider || '').slice(1)}
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {selectedAtsJob
                      ? t(
                          `Stelle: ${selectedAtsJob.title}`,
                          `Job: ${selectedAtsJob.title}`,
                        )
                      : t(
                          'Alle aktuellen Kandidat:innen.',
                          'All recent candidates.',
                        )}
                  </p>
                </div>
                <button
                  onClick={() => setShowAtsPicker(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {atsLoading && (
                  <div className="p-6 text-center text-sm text-slate-500">
                    {t('Lade aus ATS ...', 'Loading from ATS ...')}
                  </div>
                )}
                {!atsLoading && atsError && (
                  <div className="p-4 text-sm text-red-600">{atsError}</div>
                )}
                {!atsLoading && !atsError && atsCandidates.length === 0 && (
                  <div className="p-6 text-center text-sm text-slate-500">
                    {t(
                      'Keine Kandidat:innen gefunden. Stelle sicher, dass die ATS-Verbindung aktiv ist und ggf. Bewerbungen für die Stelle vorliegen.',
                      'No candidates found. Make sure the ATS connection is active and applications exist for this job.',
                    )}
                  </div>
                )}
                {!atsLoading &&
                  atsCandidates.map((c) => {
                    const display =
                      [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
                      || c.email
                      || c.ats_candidate_id;
                    return (
                      <button
                        key={c.ats_candidate_id}
                        type="button"
                        onClick={() => pickAtsCandidate(c)}
                        disabled={!c.email}
                        className="w-full text-left px-3 py-2 rounded-xl hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col"
                      >
                        <span className="text-sm font-medium text-slate-800">
                          {display}
                        </span>
                        <span className="text-[11px] text-slate-500">
                          {c.email || t('Keine E-Mail im ATS', 'No email on ATS')}
                          {c.status ? ` · ${c.status}` : ''}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          {success ? (
            <>
              <button
                onClick={reset}
                className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
              >
                {t('Weitere Einladung', 'Another invite')}
              </button>
              <button
                onClick={() => {
                  reset();
                  onClose();
                }}
                className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
              >
                {t('Schliessen', 'Close')}
              </button>
            </>
          ) : (
            <>
              {onDemo && selectedTemplate && (
                <button
                  onClick={() => onDemo({
                    template_id: templateId,
                    persona_id: effectivePersonaId,
                    language: interviewLang,
                    target_duration_minutes: duration,
                    role_seniority: roleSeniority,
                    role_title: roleTitle.trim() || selectedTemplate.title,
                  })}
                  disabled={submitting || demoStarting === templateId}
                  title={t(
                    'Gleicher Fragenpool und Ablauf wie bei Kandidat:innen (Fragen variieren leicht) -- ohne Quota.',
                    'Same question pool and flow as for candidates (questions may vary slightly) -- no quota.',
                  )}
                  className="mr-auto px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white hover:border-pink-300 hover:text-pink-600 text-slate-700 flex items-center gap-1.5 disabled:opacity-60"
                >
                  {demoStarting === selectedTemplate.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <PlayCircle className="w-4 h-4" />
                  )}
                  {t('Demo starten', 'Start demo')}
                </button>
              )}
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
              >
                {t('Abbrechen', 'Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !templateId || !email || roleTitle.trim().length < 2 || !roleSeniority}
                className="px-4 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" />
                {submitting ? t('Sende ...', 'Sending ...') : t('Einladung senden', 'Send invite')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({
  label,
  hint,
  children,
}) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
      {label}
    </label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default InviteCreateModal;
